<template>
    <div class="h-full">
        <div v-if="transaction && !transaction.documentMetaUuids.length">
            <transaction-timeline-content
                :transaction="transaction"
                :audit-entries="auditEntries"
                :all-document-metas="allDocumentMetas"
            />
        </div>
        <div
            v-else-if="transaction"
            class="flex p-2 gap-2 w-full h-full flex-col @[1200px]:flex-row"
        >
            <transaction-timeline-content
                :transaction="transaction"
                :audit-entries="auditEntries"
                :all-document-metas="allDocumentMetas"
                class="h-1/2 max-h-1/2 @[1200px]:h-full @[1200px]:max-h-full overflow-y-auto w-full @[1200px]:w-1/2 @container/tview"
            />
            <div class="h-1/2 max-h-1/2 @[1200px]:h-full @[1200px]:max-h-full overflow-y-auto w-full @[1200px]:w-1/2 @container/tview">
                <document-preview-multiple :document-meta-uuids="transaction?.documentMetaUuids" />
            </div>
        </div>
        <div v-else-if="notFound" class="flex h-full justify-center items-center">
            Beklager, finner ikke bilaget for denne virksomheten...
        </div>
    </div>
</template>

<script setup lang="ts">
import {computed, inject, onMounted, onUnmounted, ref, watch} from 'vue'
import {Transaction} from '@/types/transaction/Transaction'
import {getTransactionByUuidKey} from '@/types/transaction/Transaction.injects'
import {useEmitter} from '@/services/infrastructure/emitter'
import {ORG_SELECTED} from '@/types/events'
import {DELETE_TRANSACTION, TRANSACTION_CREATED, TRANSACTION_UPDATED} from '@/types/transaction/TransactionEvent'
import TransactionTimelineContent from '@/transaction/edit-transaction/timeline/TransactionTimelineContent.vue'
import {fetchEntityAuditTrail} from '@/activity-log/activity-log.service'
import {AuditLogEntry} from '@/types/AuditLog'
import {listDocumentsByTransactionUuid} from '@/services/document.service'
import {DocumentMeta} from '@/types/transaction/DocumentMeta'
import DocumentPreviewMultiple from '@/document/DocumentPreviewMultiple.vue'

const {onEvent, cleanUp} = useEmitter()
const props = defineProps({
    transactionUuid: {
        type: String,
        required: true
    }
})

const transactionUuid = computed(() => {
    return props.transactionUuid
})

const notFound = ref(false)
const transaction = ref<Transaction>()
const auditEntries = ref<AuditLogEntry[]>([])
const allDocumentMetas = ref<DocumentMeta[]>([])

const getTransactionByUuid = inject(getTransactionByUuidKey) as Function

const loadTransaction = async () => {
    try {
        transaction.value = await getTransactionByUuid(transactionUuid.value)
        if (transaction.value) {
            auditEntries.value = await fetchEntityAuditTrail(transactionUuid.value)
            allDocumentMetas.value = await listDocumentsByTransactionUuid(props.transactionUuid)
        }
        notFound.value = false
    } catch (e) {
        notFound.value = true
        transaction.value = undefined
        console.warn('Unable to fetch transaction', transactionUuid.value)
        //TODO: Handle 404
    }
}

watch(transactionUuid, loadTransaction)

onMounted(() => {
    loadTransaction()
    onEvent(ORG_SELECTED, loadTransaction)
    onEvent(TRANSACTION_CREATED, loadTransaction)
    onEvent(TRANSACTION_UPDATED, loadTransaction)
    onEvent(DELETE_TRANSACTION, loadTransaction)
})

onUnmounted(cleanUp)

</script>

